import React, { useEffect, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Upload,
  Divider,
  Space,
  DatePicker,
  Table,
  Modal,
  Spin,
} from 'antd'
import {
  UploadOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'
import {
  DeleteGirlsStatementDocuments,
  getChildrenList,
  GetGirlsStatementDocuments,
  GetGirlsStatementDocumentsById,
  IMG_URL,
  saveGirlsStatementDocuments,
} from '../APIComponents/AdminApi'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select
const { TextArea } = Input

const GirlStatementForm = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Girls Statement')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()

  const [childrenList, setChildrenList] = useState([])
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)

  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [recordId, setRecordId] = useState(null)

  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)

  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewData, setViewData] = useState(null)
  const [viewLoading, setViewLoading] = useState(false)

  const handleView = async (record) => {
    try {
      setViewLoading(true)

      const res = await axiosInstance.get(
        `${GetGirlsStatementDocumentsById}/${record.reg_id}`,
      )

      setViewData({
        ...res.data,
        studentName: getStudentName(record.reg_id),
      })

      setViewModalVisible(true)
    } catch (err) {
      toast.error('Failed to fetch details')
    } finally {
      setViewLoading(false)
    }
  }

  useEffect(() => {
    fetchChildren()
    fetchTableData()
  }, [])

  // ================= FETCH CHILDREN =================
  const fetchChildren = async () => {
    try {
      const res = await axiosInstance.get(getChildrenList)
      setChildrenList(res.data || [])
    } catch {
      toast.error('Failed to fetch children list')
    }
  }

  // ================= FETCH TABLE DATA =================
  const fetchTableData = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetGirlsStatementDocuments)
      setTableData(res.data || [])
    } catch {
      toast.error('Failed to fetch statement records')
    } finally {
      setTableLoading(false)
    }
  }

  // ================= GET STUDENT NAME =================
  const getStudentName = (regId) => {
    const child = childrenList.find((c) => c.id === regId)
    if (!child) return '-'
    return `${child.fname || ''} ${child.mname || ''} ${child.lname || ''}`
  }

  // ================= ADD =================
  const handleAdd = () => {
    form.resetFields()
    setIsEdit(false)
    setRecordId(null)
    setShowForm(true)
  }

  // ================= EDIT =================
  const handleEdit = async (record) => {
    try {
      setEditLoading(true)

      const res = await axiosInstance.get(
        `${GetGirlsStatementDocumentsById}/${record.reg_id}`,
      )

      const data = res.data

      // ✅ Set normal form fields
      form.setFieldsValue({
        childName: record.reg_id,
        statementDate: data.date_of_statement
          ? dayjs(data.date_of_statement)
          : null,
        writtenBy: data.statement_written_by,
        statementDescription: data.statement_description,
        takenBefore: data.taken_before_authority,
        placeOfStatement: data.place_of_statement,
      })

      // ✅ Convert documents[] → fileRows (TABLE DATA)
      const mappedFiles = (data.documents || []).map((doc, index) => ({
        key: doc.id || index,

        statementCopy: doc.scanned_copy_of_statement
          ? {
              uid: `sc-${doc.id}`,
              name: doc.scanned_copy_of_statement,
              status: 'done',
              url: IMG_URL + doc.scanned_copy_of_statement,
            }
          : null,

        relatedDocuments: doc.other_related_documents
          ? {
              uid: `rd-${doc.id}`,
              name: doc.other_related_documents,
              status: 'done',
              url: IMG_URL + doc.other_related_documents,
            }
          : null,
      }))

      // ✅ SET INTO STATE (VERY IMPORTANT)
      setFileRows(mappedFiles)

      // reset temp upload fields
      setTempFiles({
        statementCopy: [],
        relatedDocuments: [],
      })

      setIsEdit(true)
      setRecordId(data.reg_id)
      setShowForm(true)
    } catch (error) {
      console.error(error)
      toast.error('Failed to load data')
    } finally {
      setEditLoading(false)
    }
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${DeleteGirlsStatementDocuments}/${deleteId}`,
      )
      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        setDeleteModal(false)
        fetchTableData()
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found!')
      }
    } catch (err) {
      toast.error('Delete failed')
    }
  }

  // ================= CANCEL =================
  const handleCancel = () => {
    form.resetFields()
    setIsEdit(false)
    setRecordId(null)
    setShowForm(false)
    setFileRows([])
  }

  const urlToFile = async (url, filename) => {
    const response = await fetch(url)
    const blob = await response.blob()

    return new File([blob], filename, {
      type: blob.type,
    })
  }

  // ================= SUBMIT / UPDATE =================

  const handleFinish = async (values) => {
    try {
      // 🔴 Validation
      if (fileRows.length === 0) {
        toast.error('Please add at least one document')
        return
      }

      setLoading(true)

      const formData = new FormData()

      // ✅ Append reg_id ONLY for ADD
      formData.append('reg_id', values.childName)

      // ✅ Normal fields
      formData.append(
        'date_of_statement',
        values.statementDate ? values.statementDate.format('YYYY-MM-DD') : '',
      )

      formData.append('statement_written_by', values.writtenBy || '')
      formData.append(
        'statement_description',
        values.statementDescription || '',
      )
      formData.append('taken_before_authority', values.takenBefore || '')
      formData.append('place_of_statement', values.placeOfStatement || '')

      // ✅ Helper: Convert URL → File (Binary)
      const urlToFile = async (url, filename) => {
        const response = await fetch(url)
        const blob = await response.blob()
        return new File([blob], filename, { type: blob.type })
      }

      // ✅ MULTIPLE FILE APPEND (OLD + NEW → BINARY)
      for (let index = 0; index < fileRows.length; index++) {
        const row = fileRows[index]

        // ===== Statement Copy =====
        if (row.statementCopy) {
          if (row.statementCopy.originFileObj) {
            // ✅ New file
            formData.append(
              `scanned_copy_of_statement[${index}]`,
              row.statementCopy.originFileObj,
            )
          } else if (row.statementCopy.url) {
            // ✅ Old file → convert to binary
            const file = await urlToFile(
              row.statementCopy.url,
              row.statementCopy.name,
            )

            formData.append(`scanned_copy_of_statement[${index}]`, file)
          }
        }

        // ===== Related Documents =====
        if (row.relatedDocuments) {
          if (row.relatedDocuments.originFileObj) {
            formData.append(
              `other_related_documents[${index}]`,
              row.relatedDocuments.originFileObj,
            )
          } else if (row.relatedDocuments.url) {
            const file = await urlToFile(
              row.relatedDocuments.url,
              row.relatedDocuments.name,
            )

            formData.append(`other_related_documents[${index}]`, file)
          }
        }
      }

      let res

      // ✅ EDIT
      if (isEdit) {
        res = await axiosInstance.post(
          `${saveGirlsStatementDocuments}`,
          formData,
        )

        if (res.data === 'SAVED') {
          toast.success('Statement Updated Successfully!')
        } else if (res.data === 'NOTFOUND') {
          toast.warn('Not found!')
        }
      }

      // ✅ ADD
      else {
        res = await axiosInstance.post(saveGirlsStatementDocuments, formData)

        if (res.data === 'SAVED') {
          toast.success('Statement Submitted Successfully!')
        } else if (res.data === 'NOTFOUND') {
          toast.warn('Not found!')
        }
      }

      // ✅ Reset
      handleCancel()
      setFileRows([]) // clear table
      fetchTableData()
    } catch (error) {
      console.error(error)
      toast.error('Failed to submit statement')
    } finally {
      setLoading(false)
    }
  }

  // ================= FILE VALIDATION =================
  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]

    const isAllowedType = allowedTypes.includes(file.type)

    if (!isAllowedType) {
      toast.error('Only JPG, PNG, and PDF files are allowed!')
      return Upload.LIST_IGNORE
    }

    const isLt3M = file.size / 1024 / 1024 < 3

    if (!isLt3M) {
      toast.warn('File must be smaller than 3MB!')
      return Upload.LIST_IGNORE
    }

    return false // prevent auto upload
  }

  // ================= TABLE COLUMNS =================
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Student Name',
      render: (_, record) => getStudentName(record.reg_id),
    },
    {
      title: 'Statement Date',
      render: (_, record) =>
        record.date_of_statement
          ? dayjs(record.date_of_statement).format('DD-MM-YYYY')
          : '-',
    },
    {
      title: 'Written By',
      dataIndex: 'statement_written_by',
    },
    {
      title: 'Place',
      dataIndex: 'place_of_statement',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => {
                      setDeleteId(record.reg_id)
                      setDeleteModal(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  // ========================================

  const [fileRows, setFileRows] = useState([])
  const [tempFiles, setTempFiles] = useState({
    statementCopy: [],
    relatedDocuments: [],
  })

  const handleTempUpload = (field, fileList) => {
    setTempFiles((prev) => ({
      ...prev,
      [field]: fileList,
    }))
  }

  const handleAddFiles = () => {
    if (
      tempFiles.statementCopy.length === 0 ||
      tempFiles.relatedDocuments.length === 0
    ) {
      toast.error('Please upload both files')
      return
    }

    const newRow = {
      key: Date.now(),
      statementCopy: tempFiles.statementCopy[0],
      relatedDocuments: tempFiles.relatedDocuments[0],
    }

    setFileRows((prev) => [...prev, newRow])

    // reset temp
    setTempFiles({
      statementCopy: [],
      relatedDocuments: [],
    })
  }

  const handleDeleteRow = (key) => {
    setFileRows((prev) => prev.filter((item) => item.key !== key))
  }

  const fileColumns = [
    {
      title: 'Statement Copy',
      render: (_, record) => record.statementCopy?.name,
    },
    {
      title: 'Related Document',
      render: (_, record) => record.relatedDocuments?.name,
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteRow(record.key)}
        />
      ),
    },
  ]

  const handleStudentChange = async (regId) => {
    try {
      if (!regId) return

      setEditLoading(true)

      const res = await axiosInstance.get(
        `${GetGirlsStatementDocumentsById}/${regId}`,
      )

      const data = res.data

      // ❗ If no data found, reset form
      if (!data || !data.id) {
        form.resetFields([
          'statementDate',
          'writtenBy',
          'statementDescription',
          'takenBefore',
          'placeOfStatement',
        ])
        setFileRows([])
        return
      }

      // ✅ Fill form fields
      form.setFieldsValue({
        statementDate: data.date_of_statement
          ? dayjs(data.date_of_statement)
          : null,
        writtenBy: data.statement_written_by,
        statementDescription: data.statement_description,
        takenBefore: data.taken_before_authority,
        placeOfStatement: data.place_of_statement,
      })

      // ✅ Map documents → fileRows
      const mappedFiles = (data.documents || []).map((doc, index) => ({
        key: doc.id || index,

        statementCopy: doc.scanned_copy_of_statement
          ? {
              uid: `sc-${doc.id}`,
              name: doc.scanned_copy_of_statement,
              status: 'done',
              url: IMG_URL + doc.scanned_copy_of_statement,
            }
          : null,

        relatedDocuments: doc.other_related_documents
          ? {
              uid: `rd-${doc.id}`,
              name: doc.other_related_documents,
              status: 'done',
              url: IMG_URL + doc.other_related_documents,
            }
          : null,
      }))

      setFileRows(mappedFiles)
    } catch (error) {
      console.error(error)

      // If API fails → clear data
      form.resetFields([
        'statementDate',
        'writtenBy',
        'statementDescription',
        'takenBefore',
        'placeOfStatement',
      ])
      setFileRows([])
    } finally {
      setEditLoading(false)
    }
  }

  // ========================================

  return (
    <>
      <AdminHeader />

      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-3">
              Girl’s Statement & Related Documents
            </h3>
          </div>

          {!showForm && (
            <>
              <Row justify="space-between" className="mb-3">
                <Col xs={24} md={10} lg={14}></Col>
                <Col
                  xs={24}
                  md={6}
                  lg={4}
                  className="text-end px-md-2 pb-2 pb-md-0"
                >
                  {pagePermission.is_new == 1 && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAdd}
                    >
                      Add
                    </Button>
                  )}
                </Col>
                <Col xs={24} md={6} lg={6}>
                  <Input
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
              </Row>

              <Table
                dataSource={tableData.filter((item) => {
                  const search = searchText.toLowerCase()
                  const child = childrenList.find((c) => c.id === item.reg_id)

                  return (
                    child?.fname?.toLowerCase().includes(search) ||
                    child?.mname?.toLowerCase().includes(search) ||
                    child?.lname?.toLowerCase().includes(search) ||
                    item.statement_written_by?.toLowerCase().includes(search) ||
                    item.place_of_statement?.toLowerCase().includes(search)
                  )
                })}
                columns={columns}
                rowKey="id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                bordered
                loading={tableLoading}
                className="shadow rounded custom-table p-0"
                style={{
                  overflowX: 'auto',
                  border: '1px solid #dfdfdf',
                  backgroundColor: 'white',
                }}
              />
            </>
          )}

          {showForm && (
            <Spin spinning={editLoading}>
              <div className="shadow p-4 rounded bg-white">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  autoComplete="off"
                >
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={10}>
                      <Form.Item
                        label="Select Children Name"
                        name="childName"
                        rules={[{ required: true }]}
                      >
                        <Select
                          showSearch
                          disabled={isEdit}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          placeholder="Search by name / reg no"
                          onChange={(value) => handleStudentChange(value)}
                        >
                          {childrenList.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={6}>
                      <Form.Item
                        label="Date of Statement"
                        name="statementDate"
                        rules={[{ required: true }]}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="DD-MM-YYYY"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={8}></Col>

                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Statement Written By"
                        name="writtenBy"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Taken Before"
                        name="takenBefore"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                      <Form.Item
                        rules={[{ required: true }]}
                        label="Place of Statement"
                        name="placeOfStatement"
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        label="Statement Description"
                        name="statementDescription"
                        rules={[{ required: true }]}
                      >
                        <TextArea rows={4} />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* {[
                      { label: 'Statement Copy', name: 'statementCopy' },
                      {
                        label: 'Other Related Documents',
                        name: 'relatedDocuments',
                      },
                    ].map((item) => (
                      <Col xs={24} md={12} lg={6} key={item.name}>
                        <Form.Item
                          label={item.label}
                          name={item.name}
                          valuePropName="fileList"
                          getValueFromEvent={(e) =>
                            Array.isArray(e) ? e : e?.fileList
                          }
                          rules={[{ required: true }]}
                        >
                          <Upload
                            beforeUpload={validateFile}
                            accept=".jpg,.jpeg,.png,.pdf"
                            maxCount={1}
                            listType="picture"
                            style={{ width: '100%' }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{ width: '100%' }}
                            >
                              Upload (Max 3MB)
                            </Button>
                          </Upload>
                        </Form.Item>
                      </Col>
                    ))} */}

                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    {/* Statement Copy */}
                    <Col xs={24} md={8}>
                      <Form.Item label="Statement Copy">
                        <Upload
                          beforeUpload={validateFile}
                          fileList={tempFiles.statementCopy}
                          onChange={({ fileList }) =>
                            handleTempUpload('statementCopy', fileList)
                          }
                          maxCount={1}
                          accept=".jpg,.jpeg,.png,.pdf"
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    {/* Related Documents */}
                    <Col xs={24} md={8}>
                      <Form.Item label="Other Related Documents">
                        <Upload
                          beforeUpload={validateFile}
                          fileList={tempFiles.relatedDocuments}
                          onChange={({ fileList }) =>
                            handleTempUpload('relatedDocuments', fileList)
                          }
                          maxCount={1}
                          accept=".jpg,.jpeg,.png,.pdf"
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>

                    {/* Add Button */}
                    <Col xs={24} className="text-end">
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddFiles}
                      >
                        Add
                      </Button>
                    </Col>

                    {/* TABLE */}
                    <Col xs={24}>
                      <Table
                        dataSource={fileRows}
                        columns={fileColumns}
                        pagination={false}
                        rowKey="key"
                        className="shadow rounded custom-table p-0 mt-3"
                        style={{
                          overflowX: 'auto',
                          border: '1px solid #dfdfdf',
                          backgroundColor: 'white',
                        }}
                      />
                    </Col>
                  </Row>

                  <Divider />

                  <Row justify="end">
                    <Space>
                      <Button onClick={handleCancel}>Cancel</Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                      >
                        {isEdit ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* ===== DELETE MODAL ===== */}
          <Modal
            title="Confirm Delete"
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Are you sure you want to delete this record?
          </Modal>

          {/* ===== VIEW MODAL ===== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Statement Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={900}
            style={{
              top: 20,
              paddingBottom: 50,
            }}
            centered
            bodyStyle={{
              maxHeight: '75vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingBottom: 10,
            }}
          >
            <Spin spinning={viewLoading}>
              {viewData && (
                <div
                  style={{
                    backgroundColor: '#f5f7fa',
                    padding: '20px 10px',
                    borderRadius: '8px',
                  }}
                >
                  {/* ================= BASIC DETAILS ================= */}

                  <Row className="mb-3">
                    <Col span={6}>
                      <strong>Student Name :</strong>
                    </Col>
                    <Col span={18}>
                      <div className="view-box">{viewData.studentName}</div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col span={6}>
                      <strong>Date :</strong>
                    </Col>
                    <Col span={18}>
                      <div className="view-box">
                        {viewData.date_of_statement
                          ? dayjs(viewData.date_of_statement).format(
                              'DD-MM-YYYY',
                            )
                          : '-'}
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col span={6}>
                      <strong>Written By :</strong>
                    </Col>
                    <Col span={18}>
                      <div className="view-box">
                        {viewData.statement_written_by || '-'}
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col span={6}>
                      <strong>Taken Before :</strong>
                    </Col>
                    <Col span={18}>
                      <div className="view-box">
                        {viewData.taken_before_authority || '-'}
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col span={6}>
                      <strong>Place :</strong>
                    </Col>
                    <Col span={18}>
                      <div className="view-box">
                        {viewData.place_of_statement || '-'}
                      </div>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col span={6}>
                      <strong>Description :</strong>
                    </Col>
                    <Col span={18}>
                      <div className="view-box">
                        {viewData.statement_description || '-'}
                      </div>
                    </Col>
                  </Row>

                  <Divider />

                  {/* ================= DOCUMENTS TABLE ================= */}

                  <h4 style={{ marginBottom: 10 }}>Documents</h4>

                  <Divider />

                  {viewData.documents && viewData.documents.length > 0 ? (
                    <Row gutter={[16, 16]}>
                      {viewData.documents.map((doc, index) => (
                        <Col xs={24} key={doc.id || index}>
                          <div
                            style={{
                              background: '#fff',
                              padding: 15,
                              borderRadius: 8,
                              border: '1px solid #e5e7eb',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                            }}
                          >
                            {/* Row Number */}
                            <div style={{ marginBottom: 10, fontWeight: 600 }}>
                              Document #{index + 1}
                            </div>

                            {/* Statement Copy */}
                            <div style={{ marginBottom: 10 }}>
                              <strong>Statement Copy:</strong>
                              <div>
                                {doc.scanned_copy_of_statement ? (
                                  <a
                                    href={
                                      IMG_URL + doc.scanned_copy_of_statement
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {doc.scanned_copy_of_statement}
                                  </a>
                                ) : (
                                  '-'
                                )}
                              </div>
                            </div>

                            {/* Related Document */}
                            <div>
                              <strong>Other Related Document:</strong>
                              <div>
                                {doc.other_related_documents ? (
                                  <a
                                    href={IMG_URL + doc.other_related_documents}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {doc.other_related_documents}
                                  </a>
                                ) : (
                                  '-'
                                )}
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div>No documents available</div>
                  )}
                </div>
              )}
            </Spin>
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default GirlStatementForm
